import React, {Component} from 'react';
import {Modal, Button, Col, Container, Row} from 'react-bootstrap';
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import PrimeDateInput from "../../../../components/date-input/PrimeDateInput";
import RemotingService from "../../../../services/remoting-service/RemotingService";
import DateUtil from "../../../../utils/DateUtil";
import moment from "moment";

export default class AppointmentSelectionModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            appointments: [],
            startTime: moment().startOf('day').toDate(),
            endTime: moment().endOf('day').toDate(),
            selectedAppointment: null,
            loading: false,
            errorMessages: []
        };
    }

    componentDidMount() {
        if (this.props.show && this.props.patientId) {
            this.retrieveAppointments();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.show && !prevProps.show && this.props.patientId) {
            this.retrieveAppointments();
        }
    }

    handleTimeChange = (timeType, date) => {
        let updatedDate = date;
        
        // If changing start time, set it to start of the selected day
        if (timeType === 'startTime') {
            updatedDate = moment(date).startOf('day').toDate();
        }
        // If changing end time, set it to end of the selected day
        else if (timeType === 'endTime') {
            updatedDate = moment(date).endOf('day').toDate();
        }
        
        this.setState({[timeType]: updatedDate}, () => {
            if (this.props.patientId) {
                this.retrieveAppointments();
            }
        });
    }

    retrieveAppointments = () => {
        const { startTime, endTime } = this.state;
        const { patientId } = this.props;

        if (!patientId) {
            this.setState({errorMessages: ['Patient ID is required']});
            return;
        }

        this.setState({loading: true, errorMessages: []});

        RemotingService.getRemoteCall(
            'api/appointment/list',
            {
                patientId: patientId,
                startTime: moment(startTime).toISOString(),
                endTime: moment(endTime).toISOString(),
                staffSpecialities: this.props.speciality ? [this.props.speciality] : null,
                ignoredStatuses: ['CANCELLED', 'NO_SHOW', 'CONSULTED', 'COMPLETED']
            },
            (response) => {
                // Handle the response structure with data.items
                const appointments = response?.data?.items || response?.items || response || [];
                this.setState({
                    appointments: appointments,
                    loading: false
                });
            },
            (error) => {
                this.setState({
                    errorMessages: [error.message || 'Failed to retrieve appointments'],
                    loading: false
                });
            }
        );
    }

    handleAppointmentSelect = (appointment) => {
        this.setState({selectedAppointment: appointment});
    }

    handleConfirm = () => {
        if (this.state.selectedAppointment) {
            // Call the callback function passed from parent component
            if (this.props.onAppointmentSelected) {
                this.props.onAppointmentSelected(this.state.selectedAppointment);
            }
            this.handleClose();
        }
    }

    handleClose = () => {
        this.setState({
            selectedAppointment: null,
            appointments: [],
            errorMessages: []
        });
        this.props.onClose();
    }

    // Template for appointment date/time
    appointmentDateTimeTemplate = (rowData) => {
        if (rowData.start) {
            const dateTime = moment(rowData.start);
            return DateUtil.formatDate(dateTime.toDate(), "Do MMMM YYYY / hh:mm A");
        }
        return '-';
    }

    // Template for appointment status
    statusTemplate = (rowData) => {
        return rowData.status || '-';
    }

    // Template for appointment type
    typeTemplate = (rowData) => {
        return rowData.patientType || '-';
    }

    // Template for doctor name
    doctorTemplate = (rowData) => {
        if (rowData.staff) {
            return rowData.staff.name || '-';
        }
        return '-';
    }

    // Template for room
    roomTemplate = (rowData) => {
        if (rowData.room) {
            return rowData.room.name || '-';
        }
        return '-';
    }

    // Template for selection radio button
    selectionTemplate = (rowData) => {
        return (
            <input
                type="radio"
                name="appointmentSelection"
                checked={this.state.selectedAppointment && this.state.selectedAppointment.id === rowData.id}
                onChange={() => this.handleAppointmentSelect(rowData)}
                style={{cursor: 'pointer'}}
            />
        );
    }

    render() {
        const { show, patientId } = this.props;
        const { appointments, startTime, endTime, selectedAppointment, loading, errorMessages } = this.state;

        return (
            <Modal show={show} onHide={this.handleClose} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Select Appointment</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Container fluid>
                        {/* Filters Section */}
                        <Row className="mb-3">
                            <Col xs={12}>
                                <h6>Filters</h6>
                            </Col>
                            <Col xs={4}>
                                <div className="form-group">
                                    <label style={{fontSize: '14px', fontWeight: 'bold', marginBottom: '5px'}}>
                                        Patient ID:
                                    </label>
                                    <div style={{fontSize: '14px', paddingTop: '8px'}}>
                                        {patientId || 'N/A'}
                                    </div>
                                </div>
                            </Col>
                            <Col xs={4}>
                                <div className="form-group">
                                    <label style={{fontSize: '14px', fontWeight: 'bold', marginBottom: '5px'}}>
                                        Start Time:
                                    </label>
                                    <PrimeDateInput 
                                        showCalendarIcon 
                                        variant="dialog" 
                                        value={startTime}
                                        onChange={date => this.handleTimeChange('startTime', date)}
                                    />
                                </div>
                            </Col>
                            <Col xs={4}>
                                <div className="form-group">
                                    <label style={{fontSize: '14px', fontWeight: 'bold', marginBottom: '5px'}}>
                                        End Time:
                                    </label>
                                    <PrimeDateInput 
                                        showCalendarIcon 
                                        variant="dialog" 
                                        value={endTime}
                                        onChange={date => this.handleTimeChange('endTime', date)}
                                    />
                                </div>
                            </Col>
                        </Row>

                        {/* Error Messages */}
                        {errorMessages.length > 0 && (
                            <Row className="mb-3">
                                <Col xs={12}>
                                    <div className="alert alert-danger">
                                        {errorMessages.map((error, index) => (
                                            <div key={index}>{error}</div>
                                        ))}
                                    </div>
                                </Col>
                            </Row>
                        )}

                        {/* Appointments Table */}
                        <Row>
                            <Col xs={12}>
                                <h6>Available Appointments</h6>
                                {loading ? (
                                    <div className="text-center p-3">
                                        <div className="spinner-border" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <DataTable 
                                        value={appointments} 
                                        responsiveLayout="scroll"
                                        paginator 
                                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                                        rows={10}
                                        rowsPerPageOptions={[10, 25, 50]}
                                        className="p-datatable-sm"
                                        selectionMode="single"
                                        selection={selectedAppointment}
                                        onSelectionChange={(e) => this.handleAppointmentSelect(e.value)}
                                    >
                                        <Column header="Select" 
                                                body={this.selectionTemplate}
                                                style={{width: '60px', textAlign: 'center'}}/>
                                        <Column field="start" 
                                                header="Date & Time" 
                                                sortable
                                                body={this.appointmentDateTimeTemplate}/>
                                        <Column field="patientType" 
                                                header="Type" 
                                                sortable
                                                body={this.typeTemplate}/>
                                        <Column field="staff" 
                                                header="Doctor" 
                                                sortable
                                                body={this.doctorTemplate}/>
                                        <Column field="room" 
                                                header="Room" 
                                                sortable
                                                body={this.roomTemplate}/>
                                        <Column field="status" 
                                                header="Status" 
                                                sortable
                                                body={this.statusTemplate}/>
                                    </DataTable>
                                )}
                            </Col>
                        </Row>
                    </Container>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={this.handleClose}>
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={this.handleConfirm}
                        disabled={!selectedAppointment}
                    >
                        Select Appointment
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}
